import React, { useEffect, useMemo, useState } from 'react';
import {
  FaExclamationCircle,
  FaBullhorn,
  FaPaperPlane,
  FaImage,
  FaUpload,
  FaFilter,
  FaSearch,
  FaFlask,
  FaRocket,
  FaLink,
  FaListUl,
} from 'react-icons/fa';

import { getApp } from 'firebase/app';
import {
  getFunctions,
  httpsCallable,
} from 'firebase/functions';
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';

/* ========================================
   Helpers
======================================== */
const BRAND_PINK = '#FA1362';
const BRAND_ORANGE = '#FF6634';
const APP_LINK_HOST = 'https://app.apnaartist.com';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.apnaartist2.app';

const tr = (v) => (v == null ? '' : String(v).trim());
const isPhone = (s) => /^\d{8,15}$/.test(String(s || '').replace(/\D/g, ''));
const normalizeRoute = (value) => {
  const raw = tr(value);
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();
    if (parsed.protocol === 'apnaartist:' && parsed.hostname) {
      return `/${parsed.hostname}${parsed.pathname === '/' ? '' : parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    if (
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      ['app.apnaartist.com', 'www.app.apnaartist.com', 'apnaartist.com', 'www.apnaartist.com'].includes(host)
    ) {
      return `${parsed.pathname || '/home'}${parsed.search}${parsed.hash}`;
    }
  } catch {}
  return raw.startsWith('/') ? raw : `/${raw}`;
};

const KNOWN_TYPES = [
  { value: '', label: '— none (just show)' },
  { value: 'booking_request', label: 'Artist: Booking request' },
  { value: 'event_booking', label: 'Artist: Event booking' },
  { value: 'payment_update', label: 'Artist: Payment update' },
  { value: 'video_request', label: 'Mentor: Video request' },
  { value: 'chat_request', label: 'Mentor: Chat request' },
  { value: 'call_request', label: 'Mentor: Call request' },
  { value: 'news_published', label: 'News published' },
  { value: 'follow', label: 'Follow' },
  { value: 'event_new', label: 'Event new' },
  { value: 'event_live', label: 'Event live' },
];

/* ========================================
   Component
======================================== */
export default function Broadcast() {
  /* ------------ permissions ------------ */
  const [hasPermissionView, setHasPermissionView] = useState(true);
  const [hasPermissionSend, setHasPermissionSend] = useState(true);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('employee'));
    const isSuperAdmin = user?.role === 'super_admin';
    const perms = user?.permissions || [];
    setHasPermissionView(isSuperAdmin || perms.includes('view_broadcast'));
    setHasPermissionSend(isSuperAdmin || perms.includes('send_broadcast'));
  }, []);

  /* ------------ firebase ------------ */
  const app = useMemo(() => {
    try { return getApp(); } catch { return null; }
  }, []);
  const db = useMemo(() => (app ? getFirestore(app) : null), [app]);
  const fns = useMemo(() => (app ? getFunctions(app, 'asia-south1') : null), [app]);
  const storage = useMemo(() => (app ? getStorage(app) : null), [app]);

  /* ------------ UI state ------------ */
  const [composerMode, setComposerMode] = useState('campaign'); // 'campaign' | 'experiment'

  /* ------------ form state ------------ */
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState(''); // final URL (uploaded or pasted)
  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  // audience: all | artists | users | phone
  const [audience, setAudience] = useState('all');
  const [toPhone, setToPhone] = useState('');

  // optional deep link
  const [dlType, setDlType] = useState('');
  const [route, setRoute] = useState('');
  const [eventId, setEventId] = useState('');
  const [bookingDocId, setBookingDocId] = useState('');
  const [requestId, setRequestId] = useState('');
  const [forRole, setForRole] = useState(''); // 'artist' | 'user' | ''

  // optional URLs (extra data for the app)
  const [actionUrl, setActionUrl] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  /* ------------ list + filters ------------ */
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [audFilter, setAudFilter] = useState('all');

  useEffect(() => {
    if (!db) return;
    const qy = query(collection(db, 'broadcast_logs'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(qy, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLogs(arr);
    });
    return () => unsub();
  }, [db]);

  const filteredLogs = useMemo(() => {
    let arr = logs;
    if (audFilter !== 'all') {
      arr = arr.filter((l) => (l.audience || l.aud) === audFilter);
    }
    if (statusFilter !== 'all') {
      arr = arr.filter((l) => (l.status || '').toLowerCase() === statusFilter);
    }
    if (tr(search)) {
      const needle = tr(search).toLowerCase();
      arr = arr.filter((l) =>
        (l.title || '').toLowerCase().includes(needle) ||
        (l.body || '').toLowerCase().includes(needle)
      );
    }
    return arr;
  }, [logs, audFilter, statusFilter, search]);

  /* ------------ upload image to Storage ------------ */
  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
  };

  const uploadImage = async () => {
    if (!storage || !imageFile) return;
    setError('');
    setOk('');
    setImageUploading(true);
    try {
      const safeName = imageFile.name.replace(/\s+/g, '_');
      const key = `broadcast_images/${Date.now()}_${safeName}`;
      const ref = storageRef(storage, key);
      await uploadBytes(ref, imageFile);
      const url = await getDownloadURL(ref);
      setImageUrl(url);
      setOk('Image uploaded ✔');
    } catch (e) {
      setError(e?.message || 'Image upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  /* ------------ submit (calls sendBroadcast) ------------ */
  const onSend = async () => {
    setError('');
    setOk('');

    if (!hasPermissionSend) {
      setError('You do not have permission to send broadcasts.');
      return;
    }
    if (!tr(title)) {
      setError('Title is required.');
      return;
    }
    if (!tr(message)) {
      setError('Message is required.');
      return;
    }
    if (audience === 'phone' && !isPhone(toPhone)) {
      setError('Please enter a valid phone (digits only).');
      return;
    }

    // Data payload Flutter understands for deep-linking
    const resolvedRoute = normalizeRoute(route);
    const resolvedDeepLink = resolvedRoute ? `${APP_LINK_HOST}${resolvedRoute}` : '';
    const data = {
      type: tr(dlType),
      route: resolvedRoute,
      deepLink: resolvedDeepLink,
      appLink: resolvedDeepLink,
      fallbackUrl: PLAY_STORE_URL,
      playStoreUrl: PLAY_STORE_URL,
      androidPackage: 'com.apnaartist2.app',
      forRole: tr(forRole),
      eventId: tr(eventId),
      eventBookingId: tr(eventId),
      bookingDocId: tr(bookingDocId),
      requestId: tr(requestId),
      targetPhone: tr(toPhone),
      imageUrl: tr(imageUrl),
      actionUrl: tr(actionUrl),
      ticketUrl: tr(ticketUrl),
      composerMode, // campaign/experiment label
    };
    Object.keys(data).forEach((k) => (data[k] === '' || data[k] == null) && delete data[k]);

    const payload = {
      title: tr(title),
      body: tr(message),
      imageUrl: tr(imageUrl),
      audience,                  // 'all' | 'artists' | 'users' | 'phone'
      toPhone: tr(toPhone),
      data,
    };

    setSending(true);
    try {
      if (!fns) throw new Error('Functions not initialized');
      const call = httpsCallable(fns, 'sendBroadcast');
      const res = await call(payload);

      const result = res?.data || {};
      if (!result?.ok) {
        const reason = tr(result?.error || result?.reason || 'broadcast_failed');
        setError(`Broadcast failed: ${reason}`);
        return;
      }

      setOk('Campaign sent as push notification (' + (result.successCount || 0) + ' success, ' + (result.failureCount || 0) + ' failed)');
      // minimal reset
      setTitle(''); setMessage('');
      setEventId(''); setBookingDocId(''); setRequestId('');
      setRoute(''); setDlType(''); setForRole(''); setToPhone('');
      // keep imageUrl/actionUrl/ticketUrl to ease repeats
    } catch (e) {
      console.warn('sendBroadcast callable failed:', e?.message || e);

      if (!db) {
        setError('sendBroadcast failed and Firestore is not initialized to log a fallback.');
      } else {
        // Fallback: store only for in-app list (still useful)
        await addDoc(collection(db, 'notifications'), {
          type: dlType || 'news',
          title: tr(title),
          body: tr(message),
          imageUrl: tr(imageUrl),
          toPhone: audience === 'phone' ? tr(toPhone) : '',
          aud: audience === 'all' ? 'all_users'
              : (audience === 'artists' ? 'artists'
              : (audience === 'users' ? 'users' : '')),
          data,
          createdAt: serverTimestamp(),
          createdBy: 'admin_panel',
        });

        await addDoc(collection(db, 'broadcast_logs'), {
          ...payload,
          status: 'fallback_write',
          createdAt: serverTimestamp(),
        });

        setOk('Saved as in-app notification (fallback). To deliver push from top, keep functions deployed.');
      }
    } finally {
      setSending(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  /* ------------ render ------------ */
  if (!hasPermissionView) {
    return (
      <div style={blurStyle}>
        <div style={popupStyle}>
          <FaExclamationCircle size={32} color="#e53935" />
          <h3 style={{ marginTop: '1rem' }}>Access Denied</h3>
          <p style={{ color: '#666' }}>You do not have permission to access this page.</p>
          <button onClick={() => window.history.back()} style={btnStyle}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Header
        mode={composerMode}
        onSetMode={setComposerMode}
        search={search}
        onSearch={setSearch}
        audFilter={audFilter}
        onAudFilter={setAudFilter}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
      />

      {!!error && <Alert color="#e53935" text={error} />}
      {!!ok && <Alert color="#43a047" text={ok} />}

      {/* Composer Card */}
      <div style={card}>
        <h3 style={h3}>
          <FaBullhorn style={{ marginRight: 8, color: BRAND_PINK }} />
          {composerMode === 'campaign' ? 'New Campaign' : 'New Experiment'}
        </h3>

        <div style={grid2}>
          <Labeled title="Title *">
            <input
              style={input}
              maxLength={60}
              placeholder="New Artist Live Tonight!"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Labeled>

          <Labeled title="Message *">
            <input
              style={input}
              maxLength={220}
              placeholder="Catch DJ Sonic live at 8 PM!"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Labeled>
        </div>

        {/* Image picker + URL */}
        <div style={grid2}>
          <Labeled title="Image (upload)">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <label style={uploadBtn}>
                <FaUpload />
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickImage} />
              </label>
              <button
                onClick={uploadImage}
                disabled={!imageFile || imageUploading || !storage}
                style={{
                  ...btnPrimary,
                  background: BRAND_PINK,
                  opacity: (!imageFile || imageUploading || !storage) ? 0.6 : 1,
                }}
              >
                {imageUploading ? 'Uploading…' : 'Upload'}
              </button>
              {imageUrl ? (
                <a href={imageUrl} target="_blank" rel="noreferrer" title="Open image" style={ghostBtn}>
                  <FaImage />&nbsp;Preview
                </a>
              ) : null}
            </div>
            {imageFile && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                Selected: {imageFile.name} ({Math.round(imageFile.size / 1024)} KB)
              </div>
            )}
          </Labeled>

          <Labeled title="Image URL (or paste)">
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                style={{ ...input, flex: 1 }}
                placeholder="https://…"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
              {imageUrl ? (
                <a href={imageUrl} target="_blank" rel="noreferrer" title="Open image" style={ghostBtn}>
                  <FaImage />
                </a>
              ) : null}
            </div>
          </Labeled>
        </div>

        <div style={grid3}>
          <Labeled title="Audience">
            <select style={input} value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="all">All users</option>
              <option value="artists">Artists only</option>
              <option value="users">Regular users only</option>
              <option value="phone">Specific phone</option>
            </select>
          </Labeled>

          <Labeled title="Phone (if audience = phone)">
            <input
              style={input}
              inputMode="numeric"
              placeholder="9085874597"
              value={toPhone}
              onChange={(e) => setToPhone(e.target.value.replace(/\D/g, ''))}
              disabled={audience !== 'phone'}
            />
          </Labeled>

          <Labeled title="forRole (optional)">
            <select style={input} value={forRole} onChange={(e) => setForRole(e.target.value)}>
              <option value="">—</option>
              <option value="artist">artist</option>
              <option value="user">user</option>
            </select>
          </Labeled>
        </div>

        <h4 style={{ ...h4, marginTop: 18 }}>
          <FaLink style={{ marginRight: 6, color: BRAND_ORANGE }} /> Deep Link & URLs (optional)
        </h4>
        <p style={{ color: '#6b7280', margin: '6px 0 14px' }}>
          Controls where the mobile app opens when the user taps the notification.
        </p>

        <div style={grid3}>
          <Labeled title="Type">
            <select style={input} value={dlType} onChange={(e) => setDlType(e.target.value)}>
              {KNOWN_TYPES.map((t) => (
                <option key={t.value || 'none'} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Labeled>

          <Labeled title="Route (override)">
            <input
              style={input}
              placeholder="/artist-profile, /user-profile, /chatRequest…"
              value={route}
              onChange={(e) => setRoute(e.target.value)}
            />
          </Labeled>

          <Labeled title="Event ID">
            <input
              style={input}
              placeholder="abc123"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
            />
          </Labeled>

          <Labeled title="Booking Doc ID">
            <input
              style={input}
              placeholder="booking doc id"
              value={bookingDocId}
              onChange={(e) => setBookingDocId(e.target.value)}
            />
          </Labeled>

          <Labeled title="Request ID (mentor)">
            <input
              style={input}
              placeholder="request id"
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
            />
          </Labeled>

          <Labeled title="Action URL (optional)">
            <input
              style={input}
              placeholder="https://… (open on tap)"
              value={actionUrl}
              onChange={(e) => setActionUrl(e.target.value)}
            />
          </Labeled>

          <Labeled title="Ticket URL (optional)">
            <input
              style={input}
              placeholder="https://… (ticket / checkout)"
              value={ticketUrl}
              onChange={(e) => setTicketUrl(e.target.value)}
            />
          </Labeled>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button
            onClick={onSend}
            disabled={sending || !hasPermissionSend}
            style={{
              ...btnPrimary,
              background: `linear-gradient(90deg, ${BRAND_PINK}, ${BRAND_ORANGE})`,
              boxShadow: '0 6px 20px rgba(250, 19, 98, 0.20)',
              opacity: sending || !hasPermissionSend ? 0.6 : 1,
              cursor: sending || !hasPermissionSend ? 'not-allowed' : 'pointer',
              paddingInline: 18,
            }}
          >
            <FaPaperPlane style={{ marginRight: 8 }} />
            {sending ? 'Sending…' : (composerMode === 'campaign' ? 'Launch Campaign' : 'Start Experiment')}
          </button>
        </div>
      </div>

      {/* Campaigns-style Recent list */}
      <div style={{ ...card, marginTop: 18 }}>
        <h3 style={h3}>
          <FaListUl style={{ marginRight: 8, color: BRAND_PINK }} />
          Recent {composerMode === 'campaign' ? 'Campaigns' : 'Experiments'}
        </h3>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={th}>Campaign</th>
                <th style={th}>Start</th>
                <th style={th}>End</th>
                <th style={th}>Status</th>
                <th style={th}>Target</th>
                <th style={th}>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((b) => (
                <tr key={b.id} style={{ borderBottom: '1px solid #eef2f7' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 800, marginBottom: 4 }}>{b.title}</div>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>{b.body}</div>
                  </td>
                  <td style={td}>—</td>
                  <td style={td}>—</td>
                  <td style={td}>
                    <Badge
                      text={(b.status || 'completed').replace('_', ' ')}
                      color={b.status === 'sent' ? BRAND_PINK : '#6b7280'}
                    />
                  </td>
                  <td style={td}>{b.audience || b.aud || '—'}</td>
                  <td style={td}>{fmtTime(b.createdAt)}</td>
                </tr>
              ))}
              {filteredLogs.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ ...td, color: '#6b7280', textAlign: 'center' }}>
                    No broadcasts yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ========================================
   UI bits
======================================== */

function Header({
  mode, onSetMode, search, onSearch, audFilter, onAudFilter, statusFilter, onStatusFilter,
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      }}>
        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          margin: 0,
        }}>
          <FaBullhorn color={BRAND_PINK} /> Broadcast Notifications
        </h2>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => onSetMode('experiment')}
            style={{
              ...pillBtn,
              background: mode === 'experiment' ? BRAND_ORANGE : '#fff',
              color: mode === 'experiment' ? '#fff' : '#111827',
              paddingInline: 16,
            }}
          >
            <FaFlask /> New Experiment
          </button>
          <button
            onClick={() => onSetMode('campaign')}
            style={{
              ...pillBtn,
              background: mode === 'campaign' ? BRAND_PINK : '#fff',
              color: mode === 'campaign' ? '#fff' : '#111827',
              paddingInline: 16,
            }}
          >
            <FaRocket /> New Campaign
          </button>
        </div>
      </div>

      <div style={{
        marginTop: 12,
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <div style={{ position: 'relative' }}>
          <FaSearch style={{ position: 'absolute', top: 12, left: 12, color: '#6b7280' }} />
          <input
            style={{ ...input, paddingLeft: 38, minWidth: 280 }}
            placeholder="Search by title / message"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaFilter color="#6b7280" />
            <select style={input} value={audFilter} onChange={(e) => onAudFilter(e.target.value)}>
              <option value="all">Target: All</option>
              <option value="all">All users</option>
              <option value="artists">Artists</option>
              <option value="users">Users</option>
              <option value="phone">Phone</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaFilter color="#6b7280" />
            <select style={input} value={statusFilter} onChange={(e) => onStatusFilter(e.target.value)}>
              <option value="all">Status: All</option>
              <option value="sent">Sent</option>
              <option value="error">Error</option>
              <option value="no_tokens">No Tokens</option>
              <option value="fallback_write">Fallback</option>
              <option value="queued">Queued (legacy)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

function Labeled({ title, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{title}</div>
      {children}
    </label>
  );
}

function Alert({ color = '#e53935', text }) {
  return (
    <div style={{
      background: '#fff',
      borderLeft: `4px solid ${color}`,
      border: '1px solid #e5e7eb',
      color: '#111827',
      padding: '12px 14px',
      borderRadius: 10,
      marginBottom: 12,
    }}>
      {text}
    </div>
  );
}

function Badge({ text, color = BRAND_PINK }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 999,
      background: color,
      color: '#fff',
      fontSize: 12,
      fontWeight: 800,
    }}>
      {text}
    </span>
  );
}

/* ------------ styles ------------ */

const card = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '18px',
};

const h3 = { fontWeight: 800, fontSize: 18, margin: 0, marginBottom: 12 };
const h4 = { fontWeight: 800, fontSize: 15, margin: 0 };

const input = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 10,
  padding: '10px 12px',
  outline: 'none',
  fontSize: 14,
  background: '#fff',
};

const uploadBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  border: '1px dashed #d1d5db',
  background: '#fff',
  padding: '10px 12px',
  borderRadius: 10,
  cursor: 'pointer',
  color: '#111827',
};

const btnPrimary = {
  background: '#111827',
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  padding: '12px 14px',
  fontWeight: 800,
  display: 'inline-flex',
  alignItems: 'center',
};

const ghostBtn = {
  background: '#f3f4f6',
  color: '#111827',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '8px 12px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const grid2 = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};

const grid3 = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 12,
};

const th = {
  padding: '12px 14px',
  textAlign: 'left',
  fontWeight: 800,
  borderBottom: '1px solid #e5e7eb',
  fontSize: 13,
};

const td = {
  padding: '12px 14px',
  borderBottom: '1px solid #eef2f7',
  fontSize: 13,
  color: '#111827',
  verticalAlign: 'top',
};

const pillBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  border: '1px solid #e5e7eb',
  padding: '10px 16px',
  borderRadius: 999,
  background: '#fff',
  fontWeight: 800,
  cursor: 'pointer',
};

const blurStyle = {
  backdropFilter: 'blur(5px)',
  backgroundColor: 'rgba(0,0,0,0.3)',
  position: 'fixed',
  top: 0, left: 0, width: '100vw', height: '100vh',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 999,
};

const popupStyle = {
  background: '#fff',
  padding: '2rem',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  textAlign: 'center',
};

const btnStyle = {
  marginTop: '1.5rem',
  backgroundColor: '#555',
  color: '#fff',
  padding: '0.6rem 1.2rem',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

function fmtTime(ts) {
  try {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return d.toLocaleString();
  } catch {
    return '—';
  }
}

